import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ChevronLeft, ChevronRight, Plus, Trash2, X, Megaphone, Leaf } from "lucide-react";
import { getPlan, createPlanItem, updatePlanItem, deletePlanItem, CATEGORIES, TIPI } from "@/lib/api";
import { toast } from "sonner";

const MESI = ["Gennaio","Febbraio","Marzo","Aprile","Maggio","Giugno","Luglio","Agosto","Settembre","Ottobre","Novembre","Dicembre"];
const GIORNI = ["Lun","Mar","Mer","Gio","Ven","Sab","Dom"];
const STATI = ["idea", "scritto", "registrato", "pubblicato"];
const STATO_COLOR = {
  idea: "#6B7280",
  scritto: "#D97706",
  registrato: "#2563EB",
  pubblicato: "#16A34A",
};
const TIPO_COLOR = {
  Attenzione: "#D92D20",
  Educazione: "#2563EB",
  Autorevolezza: "#7C3AED",
  Conversione: "#16A34A",
};

// Fase organica giorni 1-20, sponsorizzata 21-fine
const faseFor = (giorno) => (giorno <= 20 ? "organico" : "sponsorizzata");

export default function Planner() {
  const now = new Date();
  const [anno, setAnno] = useState(now.getFullYear());
  const [mese, setMese] = useState(now.getMonth() + 1);
  const [items, setItems] = useState([]);
  const [selectedDay, setSelectedDay] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ categoria: "Tabù di coppia", tipo: "Attenzione", titolo: "", hook_fissato: "", note: "", stato: "idea" });

  const load = async () => {
    try {
      const data = await getPlan(anno, mese);
      setItems(data);
    } catch {
      toast.error("Errore nel caricamento del piano");
    }
  };

  useEffect(() => {
    load();
    setSelectedDay(null);
    setShowForm(false);
  }, [anno, mese]);

  const daysInMonth = new Date(anno, mese, 0).getDate();
  const firstWeekday = (new Date(anno, mese - 1, 1).getDay() + 6) % 7; // Mon=0

  const prevMonth = () => {
    if (mese === 1) { setMese(12); setAnno(anno - 1); } else setMese(mese - 1);
  };
  const nextMonth = () => {
    if (mese === 12) { setMese(1); setAnno(anno + 1); } else setMese(mese + 1);
  };

  const itemsForDay = (d) => items.filter((i) => i.giorno === d);
  const selectedItems = selectedDay ? itemsForDay(selectedDay) : [];

  const openDay = (d) => {
    setSelectedDay(d);
    setShowForm(false);
  };

  const submitForm = async () => {
    if (!form.titolo.trim()) { toast.error("Aggiungi un titolo"); return; }
    try {
      const created = await createPlanItem({
        anno, mese, giorno: selectedDay, fase: faseFor(selectedDay), ...form,
      });
      setItems((p) => [...p, created]);
      setForm({ categoria: "Tabù di coppia", tipo: "Attenzione", titolo: "", hook_fissato: "", note: "", stato: "idea" });
      setShowForm(false);
      toast.success("Contenuto pianificato");
    } catch {
      toast.error("Errore");
    }
  };

  const cycleStato = async (item) => {
    const idx = STATI.indexOf(item.stato);
    const next = STATI[(idx + 1) % STATI.length];
    try {
      const updated = await updatePlanItem(item.id, { stato: next });
      setItems((p) => p.map((i) => (i.id === item.id ? updated : i)));
    } catch { toast.error("Errore"); }
  };

  const removeItem = async (id) => {
    try {
      await deletePlanItem(id);
      setItems((p) => p.filter((i) => i.id !== id));
      toast.success("Rimosso");
    } catch { toast.error("Errore"); }
  };

  const cells = [];
  for (let i = 0; i < firstWeekday; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  return (
    <div className="grid grid-cols-1 xl:grid-cols-[1fr_minmax(0,360px)] gap-6">
      {/* Calendar */}
      <div className="bg-[#121212] border border-[#262626] rounded-md p-6">
        <div className="flex items-center justify-between mb-5">
          <button data-testid="prev-month" onClick={prevMonth} className="text-neutral-400 hover:text-white p-2"><ChevronLeft size={20} /></button>
          <h2 className="font-display font-extrabold text-2xl tracking-tight" data-testid="month-label">
            {MESI[mese - 1]} <span className="text-neutral-500">{anno}</span>
          </h2>
          <button data-testid="next-month" onClick={nextMonth} className="text-neutral-400 hover:text-white p-2"><ChevronRight size={20} /></button>
        </div>

        <div className="flex items-center gap-5 mb-4 text-xs">
          <span className="flex items-center gap-1.5 text-neutral-400"><Leaf size={13} className="text-neutral-500" /> Organico (1–20)</span>
          <span className="flex items-center gap-1.5 text-neutral-400"><Megaphone size={13} className="text-[#D92D20]" /> Sponsorizzata (21–fine)</span>
        </div>

        <div className="grid grid-cols-7 gap-1.5 mb-1.5">
          {GIORNI.map((g) => (
            <div key={g} className="text-center label-caps py-1">{g}</div>
          ))}
        </div>
        <div className="grid grid-cols-7 gap-1.5">
          {cells.map((d, idx) => {
            if (!d) return <div key={idx} />;
            const dayItems = itemsForDay(d);
            const sponsored = faseFor(d) === "sponsorizzata";
            const isSel = selectedDay === d;
            return (
              <button
                key={idx}
                data-testid={`day-${d}`}
                onClick={() => openDay(d)}
                className={`aspect-square rounded-sm border p-1.5 flex flex-col text-left transition-colors ${
                  isSel ? "border-white" : "border-transparent hover:border-[#333]"
                } ${sponsored ? "bg-[#991B1B]/15" : "bg-[#1A1A1A]"}`}
              >
                <span className={`text-xs font-semibold ${sponsored ? "text-[#F87171]" : "text-neutral-400"}`}>{d}</span>
                <div className="flex flex-wrap gap-1 mt-auto">
                  {dayItems.slice(0, 4).map((it) => (
                    <span key={it.id} className="w-1.5 h-1.5 rounded-full" style={{ background: STATO_COLOR[it.stato] }} />
                  ))}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Day panel */}
      <div className="bg-[#121212] border border-[#262626] rounded-md p-6 h-fit">
        {!selectedDay ? (
          <div className="text-center py-16">
            <p className="text-neutral-500">Seleziona un giorno per pianificare i contenuti.</p>
          </div>
        ) : (
          <div>
            <div className="flex items-center justify-between mb-1">
              <h3 className="font-display font-bold text-xl">
                {selectedDay} {MESI[mese - 1]}
              </h3>
              <span className={`text-[0.65rem] uppercase tracking-wider font-semibold px-2 py-1 rounded-sm ${
                faseFor(selectedDay) === "sponsorizzata" ? "bg-[#D92D20]/15 text-[#F87171]" : "bg-[#262626] text-neutral-400"
              }`}>
                {faseFor(selectedDay) === "sponsorizzata" ? "Sponsorizzata" : "Organico"}
              </span>
            </div>
            <p className="text-xs text-neutral-600 mb-5">
              {faseFor(selectedDay) === "sponsorizzata"
                ? "Fase finale: sponsorizzata Meta verso pubblico caldo."
                : "Fase organica: costruisci e riscalda il pubblico."}
            </p>

            <div className="space-y-2 mb-4" data-testid="day-items">
              {selectedItems.length === 0 && <p className="text-sm text-neutral-600">Nessun contenuto per questo giorno.</p>}
              {selectedItems.map((it) => (
                <div key={it.id} className="bg-[#1A1A1A] border border-[#262626] rounded-sm p-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-[0.6rem] uppercase tracking-wider text-neutral-500">{it.categoria}</span>
                      {it.tipo && (
                        <span className="text-[0.6rem] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded-sm border" style={{ color: TIPO_COLOR[it.tipo] || "#9CA3AF", borderColor: `${TIPO_COLOR[it.tipo] || "#666"}66` }}>
                          {it.tipo}
                        </span>
                      )}
                    </div>
                    <button data-testid={`plan-delete-${it.id}`} onClick={() => removeItem(it.id)} className="text-neutral-600 hover:text-[#D92D20]"><Trash2 size={14} /></button>
                  </div>
                  <p className="text-sm text-neutral-100 font-medium mt-1">{it.titolo}</p>
                  {it.hook_fissato && <p className="text-xs text-neutral-500 mt-1">{it.hook_fissato}</p>}
                  <button
                    onClick={() => cycleStato(it)}
                    className="mt-2 text-[0.65rem] uppercase tracking-wider font-semibold px-2 py-1 rounded-sm"
                    style={{ background: `${STATO_COLOR[it.stato]}22`, color: STATO_COLOR[it.stato] }}
                    title="Clicca per cambiare stato"
                  >
                    {it.stato}
                  </button>
                </div>
              ))}
            </div>

            {!showForm ? (
              <button
                data-testid="add-content-btn"
                onClick={() => setShowForm(true)}
                className="w-full border border-[#333] hover:border-[#D92D20] text-neutral-300 py-2.5 rounded-sm flex items-center justify-center gap-2 transition-colors text-sm font-semibold"
              >
                <Plus size={16} /> Aggiungi contenuto
              </button>
            ) : (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="border border-[#262626] rounded-sm p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="label-caps">Nuovo contenuto</span>
                  <button onClick={() => setShowForm(false)} className="text-neutral-500 hover:text-white"><X size={16} /></button>
                </div>
                <div className="flex gap-1.5">
                  {CATEGORIES.map((c) => (
                    <button
                      key={c}
                      onClick={() => setForm({ ...form, categoria: c })}
                      className={`text-[0.65rem] font-semibold py-1.5 px-2 rounded-sm border flex-1 ${
                        form.categoria === c ? "bg-[#D92D20] border-[#D92D20] text-white" : "border-[#333] text-neutral-400"
                      }`}
                    >
                      {c}
                    </button>
                  ))}
                </div>
                <div className="grid grid-cols-2 gap-1.5">
                  {TIPI.map((t) => (
                    <button
                      key={t}
                      data-testid={`plan-tipo-${t}`}
                      onClick={() => setForm({ ...form, tipo: t })}
                      className={`text-[0.65rem] font-semibold py-1.5 px-2 rounded-sm border ${
                        form.tipo === t ? "bg-white border-white text-black" : "border-[#333] text-neutral-400"
                      }`}
                    >
                      {t}
                    </button>
                  ))}
                </div>
                <input
                  data-testid="plan-titolo"
                  value={form.titolo}
                  onChange={(e) => setForm({ ...form, titolo: e.target.value })}
                  placeholder="Titolo / tema"
                  className="w-full bg-[#0A0A0A] border border-[#262626] rounded-sm p-2.5 text-sm focus:outline-none focus:border-[#D92D20]"
                />
                <input
                  value={form.hook_fissato}
                  onChange={(e) => setForm({ ...form, hook_fissato: e.target.value })}
                  placeholder="Hook fissato (opzionale)"
                  className="w-full bg-[#0A0A0A] border border-[#262626] rounded-sm p-2.5 text-sm focus:outline-none focus:border-[#D92D20]"
                />
                <button
                  data-testid="plan-save-btn"
                  onClick={submitForm}
                  className="w-full bg-[#D92D20] hover:bg-[#B42318] text-white font-bold py-2.5 rounded-sm text-sm uppercase tracking-wider"
                >
                  Pianifica
                </button>
              </motion.div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
